import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ISelectOption } from '@private/components';

import AutocompleteSelect from 'components/Controls/AutocompleteSelect';
import Input from 'components/Controls/Input';
import { INPUT_MODES } from 'components/Controls/PeriodInputs/helpers';
import {
  REG_EXP_VARIANTS,
  ROLE_SCOPE,
} from 'components/Grids/DataGrid/Filters/utils/constants';
import { FILTRATION_TYPES } from 'constants/common';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useGetFilterFieldData } from 'queries/filters';
import { CURRENCY_POSITION_BINARY_OPTIONS } from 'routes/pages/AuthorizedStack/MerchantsSettings/CashierSettings/Form/helpers';

import { useFilterContext } from '../../../../FilterContext';
import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
  INumberRange,
  ISelectFilterProps,
} from '../../../../utils/types';
import { RangeContainer } from '../../../styled';
import { BaseRemoveButton, RemoveButton } from '../styled';

import FilterInput, { getValuesList } from './components/FilterInput';
import FilterPeriodInput from './components/FilterPeriodItem';
import RoleScope from './components/RoleScope';
import ZonedDateTimePicker from './components/ZonedDateTimePicker';
import { useGetSelectOptions } from './hooks/useGetSelectOptions';

import { MultiFilters, Row } from './styled';

export const isDatePickerType = (type?: string) => {
  return (
    type === FILTRATION_TYPES.LOCAL_DATE || type === FILTRATION_TYPES.ZONED_DATE_TIME
  );
};

export interface IFilterComponent {
  appliedFilter: IAppliedFilterValue;
  columnKey: string;
  fallbackOperation: FILTER_OPERATIONS;
  isAdditional?: boolean;
  isRequestFilter: boolean;
  isSelect: boolean;
  multiSelect: boolean;
  onSelectColumnFilter?: (props: ISelectFilterProps) => void;
  onToggleFilterValue?: () => void;
  type: string;
  variant?: 'column' | 'sidebar';
}

const FilterComponent = ({
  appliedFilter,
  columnKey,
  fallbackOperation,
  isAdditional,
  isRequestFilter,
  isSelect,
  multiSelect,
  onSelectColumnFilter,
  onToggleFilterValue,
  type,
  variant = 'sidebar',
}: IFilterComponent) => {
  const { t } = useTranslation();

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [isAutoFocused, setIsAutoFocused] = useState(false);

  const {
    clearFocusedItem,
    config: {
      datePickerConfig,
      daysList,
      hoursList,
      requestFiltersInstance,
      requestFiltersPrefix,
      unlimitedHoursList,
      weeksList,
    },
    filters,
    focusedFieldName,
    onSelectFilter: onSelectSidebarFilter,
    removeFilter,
    toggleFilterEnabled,
  } = useFilterContext();

  const onTogglePerValuePower = React.useCallback(
    (index: number) => () => {
      toggleFilterEnabled({ columnKey, index, isAdditional });
      onToggleFilterValue?.();
    },
    [toggleFilterEnabled, columnKey, isAdditional, onToggleFilterValue],
  );

  const onSelectFilter = React.useCallback(
    (props: ISelectFilterProps) => {
      if (variant === 'column') {
        onSelectColumnFilter?.({ ...props, isAdditional });
      } else {
        onSelectSidebarFilter({ ...props, isAdditional });
      }
    },
    [onSelectColumnFilter, onSelectSidebarFilter, variant, isAdditional],
  );

  const {
    data: requestFilterData,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetFilterFieldData({
    enabled: autocompleteOpen,
    field: columnKey,
    instance: requestFiltersInstance,
    pageFilters: { search: filters },
    prefix: requestFiltersPrefix,
  });

  const onAutocompleteFocus = React.useCallback(() => {
    setAutocompleteOpen(isRequestFilter);

    // `enabled` never toggles back off, so an errored query needs an explicit
    // refetch — refocusing the control is the user's retry gesture
    if (isError && !isFetching) {
      refetch();
    }
  }, [isRequestFilter, isError, isFetching, refetch]);

  const { operation = fallbackOperation, value } = appliedFilter || {};

  React.useEffect(() => {
    setIsAutoFocused(
      focusedFieldName?.columnKey === columnKey &&
        !!focusedFieldName?.isAdditional === !!isAdditional,
    );
  }, [
    focusedFieldName?.columnKey,
    focusedFieldName?.isAdditional,
    columnKey,
    isAdditional,
  ]);

  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isAutoFocused) {
      setTimeout(() => ref.current?.scrollIntoView({ block: 'center' }), 300);
    }
  }, [focusedFieldName, isAutoFocused]);

  const onBlur = React.useCallback(() => {
    clearFocusedItem();
  }, [clearFocusedItem]);

  const onSelectFilterOption = React.useCallback(
    (value: unknown, option?: ISelectOption | ISelectOption[]) => {
      if (value !== undefined) {
        onSelectFilter({
          columnKey,
          operation,
          value: Array.isArray(option) && !option.length ? '' : option,
        });
      }
    },
    [onSelectFilter, columnKey, operation],
  );

  const removeMultiInputValue = React.useCallback(
    (index: number) => () => {
      const currentValue = Array.isArray(value)
        ? (value as string[])
        : ([value] as string[]);

      if (currentValue.length === 1) {
        removeFilter({ columnKey, index, isAdditional, resetCanFilter: true });
      } else {
        onSelectFilter({
          columnKey,
          operation,
          value: currentValue.filter((_, itemIndex) => index !== itemIndex),
        });
      }
    },
    [value, removeFilter, columnKey, isAdditional, onSelectFilter, operation],
  );

  const onAddColumnFilter = React.useCallback(() => {
    if (Array.isArray(value)) {
      onSelectFilter({
        columnKey,
        operation,
        value: [...(value as string[]), ''],
      });
    }
  }, [onSelectFilter, value, columnKey, operation]);

  const onMinInputFilterChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onSelectFilter({
        columnKey,
        operation,
        value: { max: (value as INumberRange)?.max, min: e.target.value },
      }),
    [onSelectFilter, value, columnKey, operation],
  );

  const onMaxInputFilterChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onSelectFilter({
        columnKey,
        operation,
        value: { max: e.target.value, min: (value as INumberRange)?.min },
      }),
    [onSelectFilter, value, columnKey, operation],
  );

  const optionsList = useGetSelectOptions({
    columnKey,
    isRequestFilter,
    requestFilterData,
    type,
  });

  const commonInputProps = {
    isAnimatedLabel: false,
    onBlur,
    regExp: REG_EXP_VARIANTS[type as keyof typeof REG_EXP_VARIANTS],
    sizeVariant: 'sm' as const,
  };

  const isUnlimitedHoursFilter = !!(columnKey && unlimitedHoursList?.has(columnKey));
  const hoursFilter = columnKey && hoursList?.has(columnKey);
  const isDaysFilter = daysList?.has(columnKey);
  const isWeeksFilter = weeksList?.has(columnKey);
  const isPeriodFilter =
    isUnlimitedHoursFilter || hoursFilter || isDaysFilter || isWeeksFilter;

  if (
    operation === FILTER_OPERATIONS.NOT_EQUALS_NULL ||
    operation === FILTER_OPERATIONS.EQUALS_NULL
  ) {
    return (
      <Input
        disabled
        inputRef={ref}
        testIds={{ input: `${columnKey}Input` }}
        value={t(
          `common.filters.${operation === FILTER_OPERATIONS.NOT_EQUALS_NULL ? 'notEqualsNull' : 'equalsNull'}`,
        )}
        {...commonInputProps}
      />
    );
  }

  if (columnKey === ROLE_SCOPE) {
    return (
      <RoleScope
        appliedFilter={appliedFilter}
        fallbackOperation={fallbackOperation}
        isAutoFocused={isAutoFocused}
      />
    );
  }

  if (!multiSelect && isPeriodFilter) {
    let inputsMode = INPUT_MODES.HOUR_MINUTE;

    if (isDaysFilter) {
      inputsMode = INPUT_MODES.DAY_HOUR_MINUTE;
    }

    if (isWeeksFilter) {
      inputsMode = INPUT_MODES.WEEK_DAY_HOUR_MINUTE;
    }

    return (
      <FilterPeriodInput
        columnKey={columnKey}
        focusedFieldName={focusedFieldName}
        inputsMode={inputsMode}
        isAutoFocused={isAutoFocused}
        isUnlimitedHoursFilter={isUnlimitedHoursFilter}
        onBlur={onBlur}
        onSelectFilter={onSelectFilter}
        operation={operation}
        value={value}
      />
    );
  }

  if (
    (isSelect || isRequestFilter) &&
    !(operation === FILTER_OPERATIONS.LIKE || operation === FILTER_OPERATIONS.NOT_LIKE)
  ) {
    const getSelectValue = () => {
      if (multiSelect) {
        return ((value as ISelectOption[]) || [])?.map((item) => item?.value) || null;
      }
      if (typeof value === 'string') {
        return value;
      }
      return (value as ISelectOption)?.value;
    };

    return (
      <AutocompleteSelect
        disableCloseOnSelect={multiSelect}
        disableUnselect={false}
        enablePortal={variant === 'column'}
        enableRemoveButton={false}
        error={isError}
        errorMessage={isError ? 'common.filters.optionsLoadError' : undefined}
        inputReadOnly={false}
        isAutoFocused={isAutoFocused}
        isInitialOpen={isAutoFocused}
        loading={isLoading}
        maxVisibleMultiselectItems={1}
        multiselect={multiSelect}
        onBlur={onBlur}
        onChange={onSelectFilterOption}
        onFocus={onAutocompleteFocus}
        options={
          columnKey === 'enableCurrencyPositionAfter'
            ? CURRENCY_POSITION_BINARY_OPTIONS
            : (optionsList as ISelectOption[])
        }
        placeholder={t('common.filters.selectFilterPlaceholder')}
        selectRef={ref}
        showError={isError}
        size='sm'
        testIds={{ input: `${columnKey}SelectInput`, root: `${columnKey}Select` }}
        value={getSelectValue()}
      />
    );
  }

  if (isDatePickerType(type)) {
    return (
      <ZonedDateTimePicker
        {...datePickerConfig}
        columnKey={columnKey}
        config={
          type === FILTRATION_TYPES.ZONED_DATE_TIME ? { returnTimeZonedDate: true } : {}
        }
        disableTooltip={variant === 'column'}
        fieldRef={ref}
        hideInput={variant === 'column'}
        isAutoFocused={isAutoFocused}
        isBackground={variant === 'sidebar'}
        onBlur={onBlur}
        onSelectFilter={onSelectFilter}
        operation={operation}
        settingsConfig={type === FILTRATION_TYPES.LOCAL_DATE ? { hideTime: true } : {}}
        sizeVariant='sm'
        type={type}
        value={value}
        variant={
          operation === FILTER_OPERATIONS.RANGE ? 'double-range-picker' : 'datepicker'
        }
      />
    );
  }

  if (operation === FILTER_OPERATIONS.RANGE) {
    return (
      <RangeContainer>
        <Input
          {...commonInputProps}
          autoFocus={isAutoFocused}
          inputRef={ref}
          isAutoFocused={isAutoFocused}
          onChange={onMinInputFilterChange}
          placeholder='0'
          testIds={{ input: `${columnKey}MinInput` }}
          value={(value as INumberRange)?.min || ''}
        />
        <Input
          {...commonInputProps}
          onChange={onMaxInputFilterChange}
          placeholder='0'
          testIds={{ input: `${columnKey}MaxInput` }}
          value={(value as INumberRange)?.max || ''}
        />
      </RangeContainer>
    );
  }

  if (
    multiSelect ||
    operation === FILTER_OPERATIONS.LIKE ||
    operation === FILTER_OPERATIONS.NOT_LIKE
  ) {
    const valuesList = getValuesList(value);
    const testIdKey = isAdditional ? `${columnKey}Additional` : columnKey;

    let inputsMode = INPUT_MODES.HOUR_MINUTE;

    if (isDaysFilter) {
      inputsMode = INPUT_MODES.DAY_HOUR_MINUTE;
    }

    if (isWeeksFilter) {
      inputsMode = INPUT_MODES.WEEK_DAY_HOUR_MINUTE;
    }

    return (
      <MultiFilters>
        {valuesList.map((_, index) => {
          const isRowDisabled = !!appliedFilter?.disabledIndexes?.includes(index);
          const isRowEmpty =
            appliedFilter?.value === undefined && appliedFilter?.operation === undefined;

          return (
            <Row key={index}>
              {isPeriodFilter ? (
                <FilterPeriodInput
                  columnKey={columnKey}
                  focusedFieldName={focusedFieldName}
                  index={index}
                  inputsMode={inputsMode}
                  isAutoFocused={isAutoFocused}
                  isUnlimitedHoursFilter={isUnlimitedHoursFilter}
                  multiSelect={multiSelect}
                  onBlur={onBlur}
                  onSelectFilter={onSelectFilter}
                  operation={operation}
                  value={valuesList}
                />
              ) : (
                <FilterInput
                  columnKey={columnKey}
                  commonInputProps={commonInputProps}
                  focusedFieldName={focusedFieldName}
                  index={index}
                  isAutoFocused={isAutoFocused}
                  multiSelect
                  onSelectFilter={onSelectFilter}
                  operation={operation}
                  value={valuesList}
                />
              )}
              <RemoveButton
                disabled={
                  appliedFilter?.value === undefined &&
                  appliedFilter?.operation === undefined
                }
                onClick={onTogglePerValuePower(index)}
                startIcon={<ICONS_MAP.Power />}
                testIds={{ container: `${testIdKey}TogglePowerButton-${index}` }}
                variant={isRowDisabled ? 'transparentDanger' : 'icon'}
              />
              <BaseRemoveButton
                disabled={isRowEmpty}
                onClick={removeMultiInputValue(index)}
                startIcon={<ICONS_MAP.Delete />}
                testIds={{ container: `${testIdKey}RemoveValueButton-${index}` }}
                variant='icon'
              />
            </Row>
          );
        })}
        {!(Array.isArray(value)
          ? !(value.length > 0 || value[value.length - 1])
          : true) && (
          <BaseRemoveButton
            disabled={Array.isArray(value) ? !value[value.length - 1] : true}
            iconSize={14}
            onClick={onAddColumnFilter}
            startIcon={<ICONS_MAP.Plus />}
            testIds={{ container: `${testIdKey}AddValueButton` }}
            variant='transparentPrimary'
          >
            {t('common.filters.addValue')}
          </BaseRemoveButton>
        )}
      </MultiFilters>
    );
  }

  return (
    <MultiFilters>
      <FilterInput
        columnKey={columnKey}
        commonInputProps={commonInputProps}
        focusedFieldName={focusedFieldName}
        isAutoFocused={isAutoFocused}
        multiSelect={multiSelect}
        onSelectFilter={onSelectFilter}
        operation={operation}
        value={value as string}
      />
    </MultiFilters>
  );
};

export default appReactMemo(FilterComponent);
